import React from 'react';
import { observer } from 'mobx-react';
import { Link } from 'ui/base/link';
import { BackButton } from 'ui/base/button/back_button';
import { PublicPage } from 'ui/base/page/public_page/public_page';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { ActiveButton } from 'ui/public/button';
import { ErrorMessage } from 'ui/public/error_message/error_message';
import { LabeledInput, EmailInput, PasswordInput } from 'ui/public/input';
import { LoginStore } from './login_store';
import LoginMessages from './login_messages';
import baseStyles from './login.module.css';
import { BasePath } from 'services/base/page_path';
import { FormSubmitEvent } from 'ui/base/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';
import { ResultKind } from 'services/base/types';
import { useHistory } from 'react-router-dom';
import { HistoryService } from 'services/base/history';
import { loadAllPangolinDomains } from 'services/pangolin/loaders';

type LoginStyles = {
  title: string;
  passwordResetLink: string;
};

const themedStyles: ThemedStyles<LoginStyles> = {
  [Theme.DAYLIGHT]: {
    title: baseStyles.daylightTitle,
    passwordResetLink: baseStyles.daylightPasswordResetLink,
  },
  [Theme.MIDNIGHT]: {
    title: baseStyles.midnightTitle,
    passwordResetLink: baseStyles.midnightPasswordResetLink,
  },
};

type BaseLoginProps = {
  store: LoginStore;
};

export const BaseLogin = observer(({ store }: BaseLoginProps) => {
  const history = useHistory();
  const accounts = useAccountsDomain();
  const handleLogin = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();
    const result = await store.busy.perform(() => store.handleLogin(accounts));
    if (result.kind === ResultKind.OK && accounts.user != null) {
      await store.busy.perform(loadAllPangolinDomains);
      const pangolin = accounts.pangolins.getDefault();
      if (accounts.user.emailVerifiedAt == null) {
        HistoryService.clear(history);
        HistoryService.replace(history, {
          kind: BasePath.EMAIL_VERIFICATION,
        });
      } else if (pangolin == null) {
        HistoryService.clear(history);
        HistoryService.replace(history, {
          kind: BasePath.ADD_PANGOLIN_SETUP,
        });
      } else {
        HistoryService.clear(history);
        HistoryService.replace(history, {
          kind: BasePath.HOME,
          pangolinID: pangolin.id,
        });
      }
    }
  }, [accounts, history, store]);

  const styles = useStyles(themedStyles);
  return (
    <PublicPage>
      <BackButton/>
      <form onSubmit={handleLogin}>
        <div className={styles.title}>
          {LoginMessages.login()}
        </div>
        <LabeledInput label={LoginMessages.email()}>
          <EmailInput store={store.email} id='email-address' name='email-address'/>
        </LabeledInput>
        <LabeledInput label={LoginMessages.password()}>
          <PasswordInput store={store.password} id='password' name='password'/>
        </LabeledInput>
        <ErrorMessage message={store.passwordError}/>
        <Link path={{ kind: BasePath.PASSWORD_RESET }} className={styles.passwordResetLink}>
          {LoginMessages.forgotPassword()}
        </Link>
        <ActiveButton busy={store.busy} type='submit'>{LoginMessages.buttonLogin()}</ActiveButton>
      </form>
    </PublicPage>
  );
});

export const Login = () => {
  const [store] = React.useState(new LoginStore());
  return <BaseLogin store={store}/>;
};
